import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../../../../hooks/use-toast";
import { handleHttpError } from "../../../../utils/utils";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import StarIcon from "../../../../assets/svgs/common/star.svg";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../../components/ui/form";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import Spinner from "../../../../components/spinner.components";
import { KycType } from "../../../../types/models/kyc.types";
import useKycApprovalsStore from "../../../../store/kyc-approvals.store";

// Creator hooks
import { useRejectGstAdminHttp } from "../../../../hooks/http/admin/kyc/creators/reject-gst.admin.http";
import { useRejectPayoutAdminHttp } from "../../../../hooks/http/admin/kyc/creators/reject-payout.admin.http";

interface RejectKycModalAdminPartialProps {
  type: KycType;
}

// Pre-filled rejection reasons for Payout Method KYC rejection, mirroring
// the Disqualify Post radio-button-prefill pattern.
const PayoutRejectReasons: Record<string, string> = {
  NAME_MISMATCH: "Your PAN and bank account names don't match.",
  INVALID_ACCOUNT: "Bank account details are incorrect.",
};

const PayoutRejectReasonLabels: Record<string, string> = {
  NAME_MISMATCH: "Name Mismatch",
  INVALID_ACCOUNT: "Invalid Account",
};

export default function RejectKycModalAdminPartial({
  type,
}: RejectKycModalAdminPartialProps) {
  const FormSchema = z.object({
    reasonKey: z.string().optional(),
    message: z.string().min(5, "Please provide a reason for rejection"),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      reasonKey: undefined,
      message: "",
    },
  });

  function handleReasonChange(reasonKey: string) {
    form.setValue("reasonKey", reasonKey, { shouldDirty: true });
    const template = PayoutRejectReasons[reasonKey];
    if (template) {
      form.setValue("message", template, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }

  const params = useParams();
  const id = params.creatorId || params.payoutMethodId;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { removeById } = useKycApprovalsStore();

  const creatorHooks = {
    [KycType.GST]: useRejectGstAdminHttp,
    [KycType.PAYOUT]: useRejectPayoutAdminHttp,
  };

  const useRejectHook = creatorHooks[type];
  const { execute: rejectKyc, fetching } = useRejectHook();

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (!id) return;

    const paramName = type === KycType.PAYOUT ? "payoutMethodId" : "creatorId";

    const response = await rejectKyc({
      body: { message: data.message },
      pathParams: {
        [paramName]: id,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    form.reset();

    // Remove from the global store
    removeById(id);

    document.getElementById("closeDialoug")?.click();
    setTimeout(() => navigate(-1), 100);
  }

  const typeLabels = {
    gst: "GST",
    payout: "Payout Method",
  };

  return (
    <DialogContent className="flex flex-col items-center gap-5 border-border w-full text-center text-primary">
      <DialogHeader className="w-full text-start">
        <div className="flex flex-col">
          <p className="flex gap-1 font-semibold text-28 leading-8 text-white">
            Reject {typeLabels[type]} Request <StarIcon />
          </p>
          <span className="text-secondary">
            Provide a reason for rejecting the creator's {typeLabels[type]} KYC
            submission.
          </span>
        </div>
      </DialogHeader>

      <div className="border-border border-b w-full" />

      <div className="flex items-center gap-4 w-full">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="relative flex flex-col space-y-8 w-full"
          >
            {type === KycType.PAYOUT && (
              <div className="flex flex-col gap-4 text-left">
                <p className="font-semibold text-base">
                  Choose a reason for rejecting payout method
                </p>
                <div className="gap-4 grid grid-cols-1 md:grid-cols-2">
                  {Object.keys(PayoutRejectReasons).map((key) => (
                    <div key={key} className="flex items-center gap-2">
                      <Input
                        type="radio"
                        id={key}
                        checked={form.watch("reasonKey") === key}
                        className="w-5 h-5 accent-primarybuttonbg"
                        value={key}
                        onChange={() => handleReasonChange(key)}
                      />
                      <Label
                        htmlFor={key}
                        className="font-medium text-sm leading-snug cursor-pointer"
                      >
                        {PayoutRejectReasonLabels[key]}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem className="text-start">
                  <FormControl>
                    <Textarea
                      className="px-6 pt-5 border border-border rounded-xl h-48 font-semibold placeholder:text-secondary text-white resize-none"
                      placeholder="Enter rejection reason here..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={fetching}
              className="bg-rejectbuttonbg border border-rejectbuttonborder w-44 h-14 font-semibold text-rejectbuttontext tracking-wider self-end"
            >
              {fetching ? <Spinner width={20} /> : "Reject"}
            </Button>
          </form>
        </Form>
      </div>
    </DialogContent>
  );
}
